import { Country, CountryEnum, type CountryEnumType } from '@sr/types';
import { findCompanyIdEntry, getCompanyIdEntry, isCSCompanyId } from './companyIdRegistry';

export { type CompanyInfo, getCompanyIdEntry, isKnownCompanyId } from './companyIdRegistry';

export const getCountryEnumFromCompanyId = (companyId: number | string): CountryEnumType =>
  getCompanyIdEntry(companyId).countryCode;

export const getCountryFromCompanyId = (companyId: number | string): Country =>
  getCompanyIdEntry(companyId).country;

export const findCountryFromCompanyId = (companyId: number | string): Country | undefined =>
  findCompanyIdEntry(companyId)?.country;

export const getCountryEnumFromCountry = (country: Country): CountryEnumType => {
  switch (country) {
    case Country.SWEDEN:
      return CountryEnum.SE;
    case Country.DENMARK:
      return CountryEnum.DK;
    case Country.FINLAND:
      return CountryEnum.FI;
    case Country.NORWAY:
      return CountryEnum.NO;
    case Country.POLAND:
      return CountryEnum.PL;
    default:
      return CountryEnum.SE;
  }
};

/** Swedish Stena Recycling partners. Fire or FNO, never Confidential. */
export const isSwedishRecycling = (dwkey: string): boolean =>
  !isConfidential(dwkey) && findCountryFromDwKey(dwkey) === Country.SWEDEN;

/** Whether a Finnish dwKey carries the EDW ledger duplicate suffix. */
export const isFinnishEdwDwkey = (dwkey: string): boolean =>
  dwkey.includes('_VENDOR') || dwkey.includes('_CUSTOMER');

/** A Finnish dwKey from the Datalake import, without the EDW ledger suffix. */
export const isFinnishOpDwkey = (dwkey: string): boolean =>
  findCountryFromDwKey(dwkey) === Country.FINLAND && !isFinnishEdwDwkey(dwkey);

/**
 * Partners whose order transactions live in Operational Storage: Confidential, Swedish and
 * Datalake Finnish.
 */
export const isDwkeyOp = (dwkey: string): boolean =>
  isConfidential(dwkey) ||
  findCountryFromDwKey(dwkey) === Country.SWEDEN ||
  isFinnishOpDwkey(dwkey);

export const isConfidential = (dwkey: string): boolean =>
  isCSCompanyId(getCompanyIdFromDwkey(dwkey));

export const getCompanyIdFromDwkey = (dwkey: string): number => {
  const companyId = dwkey.split('_')[0];

  return Number(companyId);
};

export const isDwkeyInCountry = (dwkey: string, countryCode: CountryEnumType): boolean =>
  findCompanyIdEntry(getCompanyIdFromDwkey(dwkey))?.countryCode === countryCode;

export const getCountryCodeFromDwkey = (dwkey: string): CountryEnumType => {
  const companyId = getCompanyIdFromDwkey(dwkey);

  return getCountryEnumFromCompanyId(companyId);
};

export const getCountryFromDwKey = (dwkey: string): Country => {
  const companyId = getCompanyIdFromDwkey(dwkey);

  return getCountryFromCompanyId(companyId);
};

/** Non-throwing variant of `getCountryFromDwKey`. */
export const findCountryFromDwKey = (dwkey: string): Country | undefined =>
  findCountryFromCompanyId(getCompanyIdFromDwkey(dwkey));

export const isFromCountry = (dwkey: string, country: Country): boolean =>
  findCountryFromDwKey(dwkey) === country;

export const isFromSweden = (dwkey: string): boolean => isFromCountry(dwkey, Country.SWEDEN);
export const isFromNorway = (dwkey: string): boolean => isFromCountry(dwkey, Country.NORWAY);
export const isFromDenmark = (dwkey: string): boolean => isFromCountry(dwkey, Country.DENMARK);
export const isFromFinland = (dwkey: string): boolean => isFromCountry(dwkey, Country.FINLAND);
export const isFromPoland = (dwkey: string): boolean => isFromCountry(dwkey, Country.POLAND);
